import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Stack,
  Divider,
  Autocomplete,
  Switch,
} from '@mui/material';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { type Editor } from '@ckeditor/ckeditor5-core';

import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import { WorkflowStepType } from '@bandido/shared/constants';
import { TeamResponseDto } from '@bandido/shared/dtos';

import { useGetAllTeams } from '@frontend/features/teams/queries';
import { CKEditorInstance } from '@frontend/features/cases/components/NoteEditor/types';
import { EditorContainer } from '@frontend/features/cases/components/NoteEditor/styled';

import { useWorkflow } from '../../WorkflowContext/hook';

import {
  teamChipStyles,
  modalContainerStyles,
  modalPaperStyles,
  modalHeaderStyles,
  closeIconStyles,
  modalContentStyles,
  editorStyles,
  teamsAutocompleteStyles,
} from './styles';

import { EMPTY_STEP, RenderTagProps, EDITOR_TOOLBAR_CONFIG } from './constants';
import { Field } from '../../WorkflowContext';
import { createUploadAdapterPlugin } from '@frontend/features/common/helper';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

export const SelectedStepModal = () => {
  const { selectedStep, onStepChange, onEditStepField, setSelectedStepId, onDeleteStep, cancelStep } = useWorkflow();
  const stepDetailsEditorRef = React.useRef<CKEditorInstance>({});
  const stepTemplateEditorRef = React.useRef<CKEditorInstance>({});
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data: teamsList } = useGetAllTeams();

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    onStepChange({
      id: selectedStep?.id,
      data: {
        ...(selectedStep?.data ?? EMPTY_STEP),
        [name]: name === 'isCancelStep' ? checked : value,
      },
    });
  };

  const handleToast = useCallback(
    (message: string) => {
      addToast({
        message,
        color: 'error',
      });
    },
    [addToast],
  );

  const handleTeamsChange = (_e: React.SyntheticEvent, newTeams: TeamResponseDto[]) => {
    onStepChange({
      id: selectedStep?.data.id,
      data: {
        ...(selectedStep?.data ?? EMPTY_STEP),
        teams: newTeams.map((team) => Number(team.id)),
        isCancelStep: selectedStep?.data?.isCancelStep ?? false,
      },
    });
  };

  const handleEditorChange = (field: Field) => (_: any, editor: Editor) => {
    const data = editor.getData();
    onEditStepField(String(selectedStep?.id), field, data);
  };

  const handleClose = () => setSelectedStepId(null);

  const handleDeleteStep = () => {
    if (!selectedStep?.id) return;
    onDeleteStep(selectedStep?.id);
    setSelectedStepId(null);
  };

  const renderTeamChip = (option: TeamResponseDto, { key, ...props }: RenderTagProps) => (
    <Chip
      key={key}
      label={option.name}
      sx={teamChipStyles}
      avatar={<Box sx={{ backgroundColor: option.color, width: 2, height: 2, borderRadius: '50%' }} />}
      {...props}
    />
  );

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={modalContainerStyles}>
      <Paper elevation={4} sx={modalPaperStyles}>
        <Box sx={modalHeaderStyles}>
          <Typography variant="subtitle2">{t('workflows.selectedStep.title')}</Typography>
          <IconButton
            size="small"
            aria-label="close"
            onClick={handleClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon sx={closeIconStyles} />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={modalContentStyles}>
          <Stack display="flex" flexDirection="column" gap={2}>
            <Stack gap={1}>
              <Typography variant="subtitle2">{t('workflows.selectedStep.label')}</Typography>
              <TextField
                value={selectedStep?.data.name}
                onChange={handleFieldChange}
                size="small"
                name="name"
                fullWidth
                placeholder={t('workflows.selectedStep.label')}
                variant="outlined"
              />
            </Stack>
            {selectedStep?.type === WorkflowStepType.FINAL && (
              <Stack gap={1}>
                <Typography variant="subtitle2">{t('workflows.selectedStep.isCancelStepLabel')}</Typography>
                <Switch
                  disabled={!!cancelStep && cancelStep.id !== selectedStep?.id}
                  checked={selectedStep?.data.isCancelStep}
                  onChange={handleFieldChange}
                  name="isCancelStep"
                  size="small"
                />
                {cancelStep && cancelStep.id !== selectedStep?.id && (
                  <Typography variant="caption" color="warning.main">
                    {t('workflows.selectedStep.cancelStepExists')}
                  </Typography>
                )}
              </Stack>
            )}

            <Stack gap={1}>
              <Typography variant="subtitle2">{t('workflows.selectedStep.info')}</Typography>
              <EditorContainer sx={editorStyles}>
                <CKEditor
                  editor={ClassicEditor}
                  data={selectedStep?.data.comment}
                  onReady={(editor: Editor) => {
                    stepDetailsEditorRef.current.editor = editor;
                  }}
                  onChange={handleEditorChange('comment')}
                  config={{ toolbar: EDITOR_TOOLBAR_CONFIG, extraPlugins: [createUploadAdapterPlugin(handleToast)] }}
                />
              </EditorContainer>
            </Stack>

            {selectedStep?.type === WorkflowStepType.FINAL && (
              <Stack gap={1}>
                <Typography variant="subtitle2">{t('workflows.selectedStep.mailTemplate')}</Typography>
                <EditorContainer sx={editorStyles}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={selectedStep?.data.template}
                    onReady={(editor: Editor) => {
                      stepTemplateEditorRef.current.editor = editor;
                    }}
                    onChange={handleEditorChange('template')}
                    config={{ toolbar: EDITOR_TOOLBAR_CONFIG, extraPlugins: [createUploadAdapterPlugin(handleToast)] }}
                  />
                </EditorContainer>
              </Stack>
            )}

            <Stack gap={1}>
              <Typography variant="subtitle2">{t('workflows.selectedStep.teams')}</Typography>
              <Autocomplete
                style={{ backgroundColor: 'transparent' }}
                multiple
                value={
                  teamsList?.items?.filter((team: TeamResponseDto) => selectedStep?.data.teams.includes(team.id)) || []
                }
                onChange={handleTeamsChange}
                options={teamsList?.items || []}
                getOptionLabel={(option) => option.name}
                disableCloseOnSelect
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => renderTeamChip(option, getTagProps({ index })))
                }
                renderInput={(params) => <TextField {...params} variant="outlined" />}
                sx={teamsAutocompleteStyles}
              />
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleDeleteStep} startIcon={<DeleteIcon />} color="error" variant="text">
            {t('workflows.selectedStep.delete')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
